import type { BundleTable } from './export/bundle';
import { formatValue } from './export/bundle';

/**
 * How a table will go onto a PDF page — the studio says so on the table before it is exported, by the PDF
 * writer's rules: as it is; rows and columns swapped (fewer rows than columns); or its columns cut into parts
 * that repeat the first column.
 */
export type Fit = { kind: 'fits' } | { kind: 'transposed'; parts: number } | { kind: 'split'; parts: number };

/** Printable width of an A4 page in points, less the margins. */
const PRINTABLE = { landscape: 842 - 64, portrait: 595 - 64 } as const;
/** Noto Sans at 8.5 pt: about 4.6 pt a character on average; 10 pt of padding a cell (as the PDF writer). */
const CHAR = 4.6;
const PAD = 10;
const MIN = 36;

function widths(header: string[], rows: string[][]): number[] {
  return header.map((h, c) => {
    // the PDF wraps a header over up to three lines: its longest word, or a third of it, sets the width
    const longestWord = Math.max(0, ...h.split(/\s+/).map((x) => x.length));
    let w = Math.max(longestWord, Math.ceil(h.length / 3)) * (CHAR + 0.3);
    for (const r of rows) w = Math.max(w, (r[c] ?? '').length * CHAR);
    return Math.max(MIN, w + PAD);
  });
}

function partsFor(ws: number[], width: number): number {
  if (ws.length <= 1) return 1;
  const first = ws[0];
  let parts = 1;
  let used = first;
  for (let i = 1; i < ws.length; i++) {
    if (used + ws[i] > width) {
      parts++;
      used = first;
    }
    used += ws[i];
  }
  return parts;
}

export function pdfFit(t: BundleTable, orientation: 'portrait' | 'landscape', locale: string): Fit {
  const width = PRINTABLE[orientation];
  const header = t.columns.map((c) => c.label);
  const cells = t.rows.map((r) => r.map((v, i) => formatValue(v, t.columns[i]?.format ?? 'text', locale)));
  const ws = widths(header, cells);
  if (ws.reduce((a, b) => a + b, 0) <= width) return { kind: 'fits' };
  if (t.rows.length < t.columns.length) {
    // swapped: the first column's values are the header, the old header the first column
    const tHeader = [header[0] ?? '', ...cells.map((r) => r[0] ?? '')];
    const tRows = header.slice(1).map((h, c) => [h, ...cells.map((r) => r[c + 1] ?? '')]);
    const tws = widths(tHeader, tRows);
    return { kind: 'transposed', parts: tws.reduce((a, b) => a + b, 0) <= width ? 1 : partsFor(tws, width) };
  }
  return { kind: 'split', parts: partsFor(ws, width) };
}
